"use client";

import { CardDescription, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlanBenefits } from "./plan-benefits";
import { cn, formatCurrency, formatNumber } from "@/utils/utils";
import {
  BillingPlan as BillingPlanType,
  BillingPurchaseModes,
} from "@jurni/schemas";
import { LoaderCircle } from "lucide-react";

interface BillingPlanProps {
  plan: BillingPlanType;
  onSubscribe?: () => void;
  onBookDemo?: () => void;
  isLoading?: boolean;
  isFeatured?: boolean;
  className?: string;
}

const badgeTypes: Record<string, string> = {
  popular: "Popular",
};

const getRemainingTrialDays = (plan: BillingPlanType) => {
  let trialDays = plan.trialDays;
  if (trialDays && plan.trialStartDate) {
    const trialStartDate = new Date(plan.trialStartDate);
    const now = new Date();
    const daysPassed = Math.max(
      0,
      Math.floor(
        (now.getTime() - trialStartDate.getTime()) / (1000 * 60 * 60 * 24),
      ),
    );
    trialDays = Math.max(0, trialDays - daysPassed);
  }

  return trialDays;
};

export const BillingPlan = ({
  plan,
  onSubscribe,
  onBookDemo,
  isLoading = false,
  isFeatured = false,
  className,
}: BillingPlanProps) => {
  const benefits = plan.benefits || [];
  const sessionsEntitlement = plan.entitlements.find(
    (entitlement) =>
      entitlement.type === "usage" && entitlement.name === "sessions",
  );
  const extraUnits = sessionsEntitlement?.extraUnits;
  const pricePerBatch = extraUnits?.pricePerBatch;
  const batchSize = extraUnits?.batchSize;
  const price = formatCurrency(plan.price, plan.currencyCode, false);
  const includedUnits = sessionsEntitlement?.includedUnits;
  const badgeLabel = plan.badgeType ? badgeTypes[plan.badgeType] : null;
  const isBookDemo = plan.purchaseMode === BillingPurchaseModes.BOOK_DEMO;
  const trialDays = getRemainingTrialDays(plan);

  const buttonText = isLoading
    ? "Processing..."
    : isBookDemo
      ? "Book a Demo"
      : trialDays
        ? `Get ${trialDays} Days Free Trial`
        : "Choose Plan";

  const monthlySessionItems = [
    includedUnits
      ? `${formatNumber(includedUnits)} sessions / month`
      : undefined,
    pricePerBatch && batchSize
      ? `${formatCurrency(
          pricePerBatch,
          plan.currencyCode,
          false,
        )} per ${formatNumber(batchSize)} additional sessions`
      : undefined,
  ].filter(Boolean) as string[];

  const monthlySession = includedUnits
    ? [
        {
          title: "Monthly Sessions",
          items: monthlySessionItems,
        },
      ]
    : [];

  const benefitsList = [...monthlySession, ...benefits];

  return (
    <article
      className={cn(
        "flex min-h-[350px] min-w-[260px] flex-col rounded-2xl border border-zinc-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[box-shadow,border-color,transform] duration-150 focus-within:border-zinc-300 focus-within:shadow-[0_0_0_3px_rgba(0,0,0,0.08),0_8px_22px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-[0_0_0_3px_rgba(0,0,0,0.08),0_8px_22px_rgba(0,0,0,0.08)]",
        className,
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="mb-2 flex min-h-6 items-center gap-2">
          <CardTitle className="truncate text-base font-normal leading-6 text-muted-foreground">
            {plan.name}
          </CardTitle>
          {badgeLabel && (
            <Badge
              variant="outline"
              className="shrink-0 px-2 py-0.5 text-xs font-medium text-muted-foreground"
            >
              {badgeLabel}
            </Badge>
          )}
        </div>
        <div className="mb-1 flex min-h-8 items-baseline gap-1">
          {isBookDemo ? (
            <span className="text-2xl font-medium leading-8 text-foreground">
              Custom
            </span>
          ) : (
            <>
              <span className="text-2xl font-medium leading-8 text-foreground">
                {price}
              </span>
              <span className="text-sm text-muted-foreground">
                /{plan.interval}
              </span>
            </>
          )}
        </div>
        <CardDescription className="mb-3 max-h-10 min-h-0 overflow-hidden text-sm leading-5 text-muted-foreground">
          {plan.description}
        </CardDescription>
        <Button
          className={cn(
            "mb-4 h-9 w-full flex-shrink-0 font-semibold",
            !isBookDemo &&
              "shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_2px_4px_rgba(0,0,0,0.16)]",
          )}
          variant={isBookDemo ? "outline" : "default"}
          onClick={isBookDemo ? onBookDemo : onSubscribe}
          disabled={isLoading}
        >
          {isLoading && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />}
          {buttonText}
        </Button>
        <PlanBenefits benefits={benefitsList} isSuggested={isFeatured} />
      </div>
    </article>
  );
};
